import React, { useContext, useState, useRef, useEffect } from "react"; 
import { CoreContext } from "context/CoreContext";
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import Input from 'components/Input';
import {  
    FormContent,
    FormTitle,
    FormText,
    RowChoose,
    AvatarUploadContainer,
    AvatarPreview,
    UploadButton,
    CreateProfileContainer,
    ProfileForm
} from './styled'; 

import ContainerAuthenticated from 'containers/Authenticated';
import Button from "components/Button";
import { CreateProfile, ReadProfiles } from "services/profile";
import { exposeStrapiError } from "utils";
import { parseProfile } from "utils/parser";
import { UploadImage, TestAuthenticatedEndpoint, PostImageAlternative, PrepareImageFile, TestUploadEndpoints } from 'services/api';
import { ReadObject } from 'services/storage';
import { ReadMe } from 'services/authentication';

export default function CreateProfileScreen(){ 
    const history = useHistory();
    const navigate = to => history.push(`/${to}`); 
    
    const [loading, setLoading] = useState(false);
    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState('/images/default-avatar.png');
    const fileInputRef = useRef(null);
    
    const { profiles, setProfiles } = useContext(CoreContext);
    
    // Verificar autenticação e se já existe um perfil
    useEffect(() => {
        const checkAuthentication = async () => {
            const authentication = await ReadObject('authentication');
            if (!authentication || !authentication.jwt) {
                toast.error('Você precisa estar logado para criar um perfil.');
                navigate('login');
                return;
            }
        };
        
        checkAuthentication();
        
        // Até 3 perfis (crianças) por conta — uma assinatura, a família toda.
        if (profiles && profiles.length >= 3) {
            toast.info('Você já tem o máximo de 3 perfis nesta conta.');
            navigate('dashboard/select-profile');
        }
    }, [profiles]);
    
    const [form, setForm] = useState({});
    const formValue = ref => { return form?.[ref] ? form?.[ref] : ''; };
    const changeForm = (value, ref) => { setForm({ ...form, [ref]: value }); }; 
    
    const cancel = () => { 
        navigate('dashboard/select-profile');
    };

    const valid = (verbose = false) => {  
        if(!formValue('name') || !formValue('name').length){ 
            if(verbose){ toast.error('Preencha o campo: Nome'); }
            return false; 
        }  
        if(!formValue('gender') || !formValue('gender').title){ 
            if(verbose){ toast.error('Preencha o campo: Sexo'); }
            return false; 
        }  
        return true;
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setAvatarFile(file);
            const previewURL = URL.createObjectURL(file);
            setAvatarPreview(previewURL);
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };
    
    // Adicione uma referência para rastrear se o componente está montado
    const isMounted = useRef(true);
    
    // Limpe quando o componente for desmontado
    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);
    
    const saveProfile = async () => {
        if (!valid(true)) return;
        
        setLoading(true);
        
        try {
            // 🔍 DEBUG: Testar se o token funciona para outros endpoints
            console.log('🔍 Testando autenticação antes do upload...');
            const testAuth = await TestAuthenticatedEndpoint();
            console.log('🔍 Teste de autenticação:', testAuth);
            
            if (!testAuth || testAuth.error) {
                throw new Error('Token inválido ou expirado');
            }
            
            // 🔍 DEBUG: Testar quais endpoints de upload estão disponíveis
            const endpointTests = await TestUploadEndpoints();
            console.log('🔍 Testes de endpoints:', endpointTests);
            
            // Declarar variáveis de upload no escopo da função
            let uploadResult = null;
            let uploadSuccessful = false;
            
            // Upload da imagem se selecionada
            if (avatarFile) {
                console.log('🔍 Iniciando upload da imagem...');
                
                try {
                    // Primeira tentativa com endpoint /upload (endpoint padrão do Strapi)
                    uploadResult = await PostImageAlternative(avatarFile);
                    uploadSuccessful = true;
                    console.log('🔍 Upload bem-sucedido via /upload!');
                } catch (uploadError) {
                    console.log('🔍 Falha no upload principal (/upload), tentando endpoint alternativo (/profile-pictures)...', uploadError.message);
                    
                    try {
                        // Segunda tentativa com endpoint /profile-pictures
                        uploadResult = await UploadImage(avatarFile);
                        uploadSuccessful = true;
                        console.log('🔍 Upload bem-sucedido via /profile-pictures!');
                    } catch (altError) {
                        console.log('🔍 Falha também no endpoint alternativo:', altError.message);
                        console.warn('🔍 Upload de imagem falhou, criando perfil sem imagem');
                        uploadSuccessful = false;
                        // Não fazer throw aqui - criar perfil sem imagem
                    }
                }
                
                // Verifique se o componente ainda está montado
                if (!isMounted.current) return;
                
                if (uploadSuccessful && uploadResult) {
                    console.log('🔍 Upload bem-sucedido! Estrutura da resposta:', uploadResult);
                } else {
                    console.log('🔍 Perfil será criado sem imagem devido a falhas no upload');
                    toast.warning('Perfil será criado sem imagem. Você pode adicionar uma imagem depois.');
                }
            }
            
            // Criar o perfil
            // Criar o perfil usando o campo picture (relacionamento com upload)
            const profileData = {
                name: form.name,
                gender: form.gender.type
            };
            
            // Se upload foi bem-sucedido, adicionar o picture ID (aceita array ou objeto)
            if (uploadSuccessful && uploadResult) {
                const uploaded = Array.isArray(uploadResult) ? uploadResult[0] : uploadResult;
                if (uploaded?.id) {
                    profileData.picture = uploaded.id;
                    console.log('🔍 Salvando perfil com picture ID:', uploaded.id);
                }
            }
            
            const result = await CreateProfile({
                data: profileData
            });
            
            // Verifique se o componente ainda está montado
            if (!isMounted.current) return;
            
            if (result?.id && !exposeStrapiError(result)) {
                const profiles = await ReadProfiles();
                
                // Verifique novamente se o componente está montado
                if (!isMounted.current) return;
                
                if (profiles?.length) { 
                    setProfiles(profiles.map(parseProfile));
                }
                
                setForm({});
                toast.success('Perfil criado com sucesso!');
                navigate('dashboard/select-profile');
            }
        } catch (err) {
            // Verifique se o componente ainda está montado antes de atualizar o estado
            if (isMounted.current) {
                console.error('Erro ao criar perfil:', err);
                
                // Se for erro de autenticação, redirecionar para login
                if (err.message.includes('Sessão expirada') || err.message.includes('não autenticado') || err.message.includes('Token inválido')) {
                    toast.error('Sua sessão expirou. Redirecionando para login...');
                    setTimeout(() => {
                        navigate('login');
                    }, 2000);
                } else if (err.message.includes('upload') || err.message.includes('403')) {
                    toast.error('Erro no upload da imagem. O perfil foi criado sem imagem.');
                    console.error('Detalhes do erro de upload:', err.message);
                } else {
                    toast.error('Ocorreu um erro ao criar o perfil. Tente novamente.');
                }
            }
        } finally {
            // Verifique se o componente ainda está montado antes de atualizar o estado
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    return ( 
        <ContainerAuthenticated primary page="CreateProfile">
            <CreateProfileContainer>
                <ProfileForm>
                    <FormTitle>Criar Seu Perfil</FormTitle>
                    
                    <FormContent>
                        <FormText>Configure seu perfil</FormText>
                        
                        {/* Campo de nome */}
                        <Input 
                            outline 
                            placeholder="Nome" 
                            value={formValue('name')} 
                            onChange={e => changeForm(e.target.value, 'name')} 
                        />
                        
                        {/* Upload de avatar */}
                        <AvatarUploadContainer>
                            <AvatarPreview image={avatarPreview} />
                            <UploadButton type="button" onClick={triggerFileInput}>
                                Escolher Imagem
                            </UploadButton>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept="image/*"
                                style={{ display: 'none' }}
                            />
                        </AvatarUploadContainer>
                        
                        {/* Seleção do sexo */}
                        <FormText label={true}>...</FormText>
                        <RowChoose>
                            {
                                [
                                    { title:'Masculino', type:"male" },
                                    { title:'Feminino', type:"female" }
                                ].map((item, key) => 
                                    <Button 
                                        key={key} 
                                        secondary={item.type === 'female'} 
                                        accent={item.type === 'male'} 
                                        outline={formValue('gender')?.title !== item.title} 
                                        onClick={() => changeForm(item, 'gender')}
                                    >
                                        {item.title}
                                    </Button>
                                )
                            }
                        </RowChoose>
                    </FormContent>

                    {/* Botões de ação */}
                    <FormContent>
                        <Button primary outline onClick={cancel}>Cancelar</Button>
                        <Button 
                            primary={valid()} 
                            onClick={saveProfile} 
                            loading={loading}
                            disabled={!valid() || loading}
                        >
                            Criar Perfil
                        </Button>
                    </FormContent>
                </ProfileForm>
            </CreateProfileContainer>
        </ContainerAuthenticated>
    );
} 